import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CaretLeft, CaretRight, PencilSimpleLine, ListChecks, LockSimple, Info, BookOpenText } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { formatDate, formatTime, formatDateTime } from "../../lib/format";
import { MonthDaysPanel, MonthStatusBadge } from "../../components/regularisation/MonthDaysPanel";
import { REQ_STATUS_CLS, currentPeriodIST, shiftPeriod, periodLabel, dayHoursLabel } from "../../lib/regularisation";
import { UserGuideDialog } from "../../components/regularisation/UserGuideDialog";

const CAT_CLS = {
    worked: "text-emerald-700", absence: "text-rose-700", pending: "text-amber-700", leave: "text-sky-700",
    weekly_off: "text-slate-400", holiday: "text-indigo-600", today: "text-slate-500",
};

function StatusExplainer({ m }) {
    if (m.month_status === "Finalised") {
        return <>Finalised by {m.finalisation?.finalised_by || "HR"} on {formatDateTime(m.finalisation?.finalised_at)}. Payroll uses <b>{m.payroll.paid_days}</b> paid days. Ask HR to reopen if something is wrong.</>;
    }
    if (m.month_status === "Awaiting Approval") {
        return <>{m.awaiting_approver} request{m.awaiting_approver > 1 ? "s are" : " is"} with your approver. Figures are provisional until HR finalises the month.</>;
    }
    return <>Figures are provisional until HR finalises the month. Regularise any date that is wrong before {formatDate(m.deadlines.employee_deadline)}.</>;
}

function PendingTable({ m, selected, setSelected, locked }) {
    const actionable = m.pending_dates.filter((p) => p.pending_with === "employee" && !p.request_id);
    const allOn = actionable.length > 0 && actionable.every((p) => selected.includes(p.date));
    if (!m.pending_dates.length) {
        return <div className="text-sm text-emerald-700 py-3" data-testid="pending-empty">Nothing pending for this month.</div>;
    }
    return (
        <table className="w-full text-sm" data-testid="pending-table">
            <thead className="text-xs text-slate-500 bg-slate-50">
                <tr>
                    <th className="w-10 px-3 py-2">
                        {!locked && actionable.length > 0 && <Checkbox checked={allOn} onCheckedChange={(v) => setSelected(v ? actionable.map((p) => p.date) : [])} data-testid="pending-select-all" />}
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Recorded</th>
                    <th className="text-left px-3 py-2 font-medium">Why it needs action</th>
                    <th className="text-left px-3 py-2 font-medium">Pending with</th>
                </tr>
            </thead>
            <tbody>
                {m.pending_dates.map((p) => {
                    const canPick = !locked && p.pending_with === "employee" && !p.request_id;
                    return (
                        <tr key={p.date} className="border-t border-slate-100" data-testid={`pending-row-${p.date}`}>
                            <td className="px-3 py-2">{canPick && <Checkbox checked={selected.includes(p.date)} onCheckedChange={(v) => setSelected((s) => v ? [...s, p.date] : s.filter((x) => x !== p.date))} data-testid={`pending-check-${p.date}`} />}</td>
                            <td className="px-3 py-2 font-mono text-xs">{formatDate(p.date)}</td>
                            <td className="px-3 py-2 text-xs">{p.day_status}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{p.reason}</td>
                            <td className="px-3 py-2 text-xs">
                                {p.pending_with === "approver" ? <Badge className="bg-amber-100 text-amber-800 text-[10px]">With {p.approver}</Badge>
                                    : p.request_status === "Draft" ? <Badge className="bg-slate-100 text-slate-600 text-[10px]">Your draft</Badge>
                                    : <Badge className="bg-indigo-50 text-indigo-700 text-[10px]">You</Badge>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function DayList({ m, locked, onRegularise }) {
    return (
        <table className="w-full text-sm" data-testid="day-list">
            <thead className="text-xs text-slate-500 bg-slate-50">
                <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">In</th>
                    <th className="text-left px-3 py-2 font-medium">Out</th>
                    <th className="text-right px-3 py-2 font-medium">Hours</th>
                    <th className="text-left px-3 py-2 font-medium">Request</th>
                    <th className="w-28" />
                </tr>
            </thead>
            <tbody>
                {m.day_list.map((d) => {
                    const active = d.request && ["Pending", "Approved"].includes(d.request.status);
                    return (
                        <tr key={d.date} className="border-t border-slate-100" data-testid={`day-row-${d.date}`}>
                            <td className="px-3 py-1.5 font-mono text-xs">{formatDate(d.date)} <span className="text-slate-400">{new Date(d.date).toLocaleDateString("en-IN", { weekday: "short" })}</span></td>
                            <td className={`px-3 py-1.5 text-xs font-medium ${CAT_CLS[d.category] || ""}`}>{d.status}{d.is_regularized && <span className="ml-1 text-violet-700">(regularised)</span>}</td>
                            <td className="px-3 py-1.5 text-xs">{d.check_in_time ? formatTime(d.check_in_time) : "—"}</td>
                            <td className="px-3 py-1.5 text-xs">{d.check_out_time ? formatTime(d.check_out_time) : "—"}</td>
                            <td className="px-3 py-1.5 text-xs text-right tabular-nums" data-testid={`day-hours-${d.date}`}>{dayHoursLabel(d.recorded_minutes ?? d.net_working_minutes, d.credited_minutes)}</td>
                            <td className="px-3 py-1.5 text-xs">{d.request ? <Badge className={`text-[10px] ${REQ_STATUS_CLS[d.request.status]}`}>{d.request.status}</Badge> : ""}</td>
                            <td className="px-3 py-1.5 text-right">
                                {!locked && !active && !["weekly_off", "holiday"].includes(d.category) && (
                                    <button type="button" className="text-xs text-indigo-700 hover:underline" onClick={() => onRegularise([d.date])} data-testid={`day-regularise-${d.date}`}>Regularise</button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default function MyMonthPage() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const period = params.get("period") || currentPeriodIST();
    const [m, setM] = useState(null);
    const [selected, setSelected] = useState([]);
    const [showDays, setShowDays] = useState(false);
    const [guide, setGuide] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/my-month", { params: { period } });
            setM(data);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err, "Could not load your month"));
        }
    }, [period]);
    useEffect(() => { load(); }, [load]);

    const locked = m?.month_status === "Finalised";
    const go = (p) => setParams({ period: p });
    const regularise = (dates) => navigate(`/regularization/new?dates=${dates.join(",")}`);
    const isFuture = useMemo(() => period > currentPeriodIST(), [period]);

    return (
        <div className="max-w-[1200px]" data-testid="my-month-page">
            <PageHeader
                className="bg-white"
                eyebrow="My Attendance & Regularisation"
                title={periodLabel(period)}
                description={m ? <span className="flex flex-wrap items-center gap-2"><MonthStatusBadge status={m.month_status} /><span><StatusExplainer m={m} /></span></span> : "Loading…"}
                actions={<>
                    <Button variant="outline" size="sm" onClick={() => go(shiftPeriod(period, -1))} data-testid="month-prev"><CaretLeft size={14} /></Button>
                    <Button variant="outline" size="sm" disabled={isFuture || period === currentPeriodIST()} onClick={() => go(shiftPeriod(period, 1))} data-testid="month-next"><CaretRight size={14} /></Button>
                    <Button variant="outline" size="sm" onClick={() => navigate("/regularization/my")} data-testid="month-my-requests"><ListChecks size={14} className="mr-1" /> My requests</Button>
                    <Button variant="outline" size="sm" className="border-orange-600 bg-orange-600 text-white font-semibold hover:bg-orange-700 hover:border-orange-700 hover:text-white" onClick={() => setGuide(true)} data-testid="month-user-guide"><BookOpenText size={14} className="mr-1" /> User Guide</Button>
                </>}
            />
            <UserGuideDialog open={guide} onClose={() => setGuide(false)} />
            {m && (
                <div className="px-6 sm:px-8 py-6 space-y-6">
                    <div className="flex flex-wrap gap-3 text-xs" data-testid="month-deadlines">
                        <span className={`rounded-md border px-2.5 py-1 ${m.deadlines.overdue_employee ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-white text-slate-700"}`}>
                            Your deadline: <b>{formatDate(m.deadlines.employee_deadline)}</b>{m.deadlines.overdue_employee && " · overdue — still submit, pending dates are not treated as absence"}
                        </span>
                        <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-slate-700">Approver deadline: <b>{formatDate(m.deadlines.manager_deadline)}</b></span>
                        {m.deadlines.extended && <span className="rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-indigo-800">Extended: {m.deadlines.extension_reason}</span>}
                        <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-slate-700" data-testid="month-paid-days">
                            Payroll {m.payroll.basis === "finalised" ? "paid" : "projected paid"} days: <b>{m.payroll.paid_days ?? "—"}</b> · LOP <b>{m.payroll.lop_days ?? "—"}</b>
                        </span>
                    </div>

                    <MonthDaysPanel month={m} />

                    <section className="rounded-xl border border-slate-200 bg-white" data-testid="pending-section">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
                            <div>
                                <h3 className="text-sm font-semibold text-slate-900">Dates needing action ({m.pending_dates.length})</h3>
                                <p className="text-xs text-slate-500">Select several dates to send one request with the same reason — each date keeps its own decision.</p>
                            </div>
                            {locked ? (
                                <span className="text-xs text-slate-500 flex items-center gap-1"><LockSimple size={13} /> Month finalised</span>
                            ) : (
                                <Button size="sm" disabled={!selected.length} onClick={() => regularise(selected)} data-testid="regularise-selected-btn">
                                    <PencilSimpleLine size={14} className="mr-1" /> Regularise {selected.length || ""} selected
                                </Button>
                            )}
                        </div>
                        <div className="px-1 pb-2 overflow-x-auto"><PendingTable m={m} selected={selected} setSelected={setSelected} locked={locked} /></div>
                    </section>

                    <section className="rounded-xl border border-slate-200 bg-white">
                        <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-900" onClick={() => setShowDays((v) => !v)} data-testid="toggle-day-list">
                            Day-by-day ({m.day_list.length} days{m.covered_through ? ` to ${formatDate(m.covered_through)}` : ""})
                            <span className="text-xs text-indigo-700 font-normal">{showDays ? "Hide" : "Show"}</span>
                        </button>
                        {showDays && <div className="px-1 pb-2 overflow-x-auto"><DayList m={m} locked={locked} onRegularise={regularise} /></div>}
                    </section>
                    <p className="text-[11px] text-slate-500 flex items-center gap-1"><Info size={12} /> All times in IST. Hours are for information only and are never converted into salary deductions or overtime.</p>
                </div>
            )}
        </div>
    );
}
